import { forwardRef, memo, useContext, useState } from "react";
import { ObjectContext } from "@providers/ObjectProvider";
import Avatar from '@mui/joy/Avatar';
import Chip from '@mui/joy/Chip';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import ButtonGroup from '@mui/joy/ButtonGroup';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import CardOverflow from '@mui/joy/CardOverflow';
import CardActions from '@mui/joy/CardActions';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import SvgIcon from '@mui/joy/SvgIcon';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import UnfoldLessRoundedIcon from '@mui/icons-material/UnfoldLessRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import RadioButtonCheckedRoundedIcon from '@mui/icons-material/RadioButtonCheckedRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';

import Tooltip from '@mui/joy/Tooltip';
import Table from '@mui/joy/Table';
import Stack from '@mui/joy/Stack';
import ObjectRelationshipCard from '@components/objects/ObjectRelationshipCard';
import { gradientTint } from "@utils/gradientTint";
import { useFormContext } from "react-hook-form";
import SearchSelect from "@components/SearchSelect";
import { endpointForMorphType, morphTypeForEndpoint } from "@utils/morphTypes";
import { Accordion, AccordionDetails, accordionDetailsClasses, AccordionSummary, Checkbox, Input, ListDivider, ListItemContent, ListItemDecorator, Option, Select, Switch, Textarea } from "@mui/joy";
import { DeleteRowButton } from "@components/objects/DeleteRowButton";
import { AddRowButton } from "@components/objects/AddRowButton";
import { DatePicker } from "@components/forms/DatePicker";
import { dateFromTime, ensureDateObject, formatDateForDatabase, formatTimeForDatabase } from "@utils/dates";
import { useAuth } from "@providers/AuthProvider";
import { TimePicker } from "@components/forms/TimePicker";
import { DynamicControl } from "@components/forms/DynamicControl";
import { additionalSx } from "@utils/additionalSx";
import TagSelect from "@components/TagSelect";

interface BaseColumn {
  label: string;
  class?: string;


  type?: string;
  endpoint?: string;
  editable?: boolean;
  name?: string;
  options?: object[];
}

interface ColumnWithValue extends BaseColumn {
  // label: string;
  value: string;//|number|null;
  // class?: string;
  transformer?: never;
} //& CommonColumn

interface ColumnWithTransformer extends BaseColumn {
  // label: string;
  value?: never;
  // class?: string;
  transformer: Function;
}

type IColumn = ColumnWithValue | ColumnWithTransformer;

const RequiredAsterisk = () => {
  return (
    <Typography
      component={'sup'}
      display={'inline'}
      textColor="#CC0000"
      aria-label="Required"
    >
      *
    </Typography>
  )
}

const RapidReinvestmentSection = memo(forwardRef(function RapidReinvestmentSection({
  activeSection,
  index,
  setActiveSection,
  completed = false,


  children = null,

  // data = null, // parent object
  // setRelationship,
  relationship = '',//[],
  title,

  startDecorator = null,
  endDecorator = null,


  description = null,
  expanded = false,
  tint = null,
  sort = null,
  columns,
  newRowData = null,
  sx = null,
  ...restProps
}, ref) {
  const { user } = useAuth();
  const { control, register, setValue, getValues, watch } = useFormContext();
  const data = watch(relationship) ?? [];

  const paginatedRelationship = data;

  let isTableEnabled = activeSection === index;//true;
  if(restProps.hasOwnProperty('enabled')) {
    isTableEnabled = typeof restProps.enabled === 'function' ? restProps.enabled(getValues()) : restProps.enabled;
  }

  const addRow = (e) => {
    e.stopPropagation();

    let newRow = {
      is_unsaved: true,
    };

    if(newRowData !== null) {
      try {
        newRow = {
          ...newRow,
          ...newRowData({
              user,
              relationship,
              data,
          }),
        };
      } catch(e) {
        //
      }
    }

    setValue(relationship, [newRow, ...data]);
  }

  const markRowForDeletion = (rowIndex, mark = true) => {
    // let _data = data;
    // console.log(data[rowIndex]);
    data[rowIndex].to_be_deleted = mark;

    // if it's empty, just delete it
    const { to_be_deleted, is_unsaved, ...otherProps } = data[rowIndex];
    // console.log(otherProps);
    if(data[rowIndex].is_unsaved && !Object.keys(otherProps).length) {
      data.splice(rowIndex, 1);
    }

    setValue(relationship, data);
  }

  // this is the primary value for a row w/ pivot
  const setRowValue = (rowIndex, key, value) => {
    // console.log('setRowValue', key, value);

    /*
    // for clearing
    if(data[rowIndex] === null) {
      data[rowIndex] = {
        is_unsaved: true
      }
      setValue(relationship, data);
      return;
    }
    */

   // null key is for primary objects that may have pivot data
   if(key === null) {
      const { to_be_deleted, is_unsaved, pivot } = data[rowIndex];
      data[rowIndex] = value ?? {};
      data[rowIndex].to_be_deleted = to_be_deleted;
      data[rowIndex].is_unsaved = is_unsaved;
      if(pivot && Object.keys(pivot).length) { // only set this if there's a value to set it to
        data[rowIndex].pivot = pivot;
      }
    } else {
      // check pivot
      // console.log(typeof key);
      if(~key.indexOf('pivot.')) {
        const pivotName = key.split('pivot.')[1];
        if(!data[rowIndex].pivot) {
          data[rowIndex].pivot = {};
        }

        data[rowIndex].pivot[pivotName] = value;
      }
      // standard
      else
      {
        data[rowIndex][key] = value;
      }
    }

    setValue(relationship, data);
  };

  /*
  const setRowPivotValue = (rowIndex, pivotName, value) => {
    if(!data[rowIndex].pivot) {
      data[rowIndex].pivot = {};
    }
    data[rowIndex].pivot[pivotName] = value;
    // console.log(data[rowIndex].pivot);
    setValue(relationship, data);
  };
  */

  // hide invisible columns
  columns = columns.filter(c => c.visibleOnEdit !== false);

  // properly set required
  columns = columns.map(column => {
    if(!!column?.required && !column?.config?.required) {
      column.config = {
        ...(column?.config ?? {}),
        required: column.required,
      }
    }

    return column;
  });

  const isColumnRequired = (column) => {
    return !!column?.required || !!column?.config?.required;
  }

  const AddAction = (props) => (restProps.hasOwnProperty('customAction')
    ? <>{restProps.customAction}</>
    : <AddRowButton disabled={!isTableEnabled} onClick={addRow} {...props} />
  );

  const EmptyAddAction = () => (restProps.hasOwnProperty('customEmptyAction')
    ? <>{restProps.customEmptyAction}</>
    : <AddRowButton disabled={!isTableEnabled} onClick={addRow} includeText/>
  );

  return (
    <Accordion
      ref={ref}
                sx={{borderRadius: 'md', boxShadow: 'md'}}
                expanded={activeSection === index}
                onChange={(event, expanded) => {
                    setActiveSection(expanded ? index : null);
                }}
            >
                <AccordionSummary
                    indicator={<Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        {columns.length > 0 ?
                        <AddAction
                            // oversized hitbox
                            sx={{
                                transition: '2s',
                                ...(expanded ? {opacity: 1} : {opacity: 0}),
                                '&:before': {
                                    content: `" "`,
                                    display: 'block',
                                    position: 'absolute',
                                    zIndex: '-0.5rem',
                                    top: '-0.5rem',
                                    right: '-0.5rem',
                                    bottom: '-0.5rem',
                                    left: '-0.5rem',
                                }
                            }}
                        />
                        : <AddRowButton disabled={true} sx={{opacity: 0, pointerEvents: 'none'}} />}
                        <KeyboardArrowDownIcon className="arrow" />
                    </Box>}
                    color={tint}
                    variant={activeSection === index ? 'solid' : 'soft'}
                    // variant="solid"
                    sx={theme => ({
                        // ...(index == 0 ? {borderTopLeftRadius: 'sm'} : {}),
                        py: 0,//1,
                        color: 'white',
                        fontWeight: '600',
                        '.MuiAccordionSummary-button': {
                            backgroundColor: `${theme.palette[tint][activeSection === index ? 600 : 600]} !important`,
                            color: '#fff',
                            borderRadius: 'sm',
                        },
                        '.MuiAccordionSummary-button:hover': {
                            // bgcolor: activeSection === index ? `${tint}.600` : `${tint}.500`,
                            backgroundColor: `${theme.palette[tint][activeSection === index ? 600 : 500]} !important`,
                        }
                    })}
                >
                    <ListItemDecorator
                    sx={theme => ({
                        '--Icon-color': theme.palette[tint][100],
                        'svg': {
                            marginRight: 1,
                        }
                    })}
                >
                    {/* {activeTab === relationship && (<RadioButtonCheckedRoundedIcon/>)} */}
                    {/* {activeTab !== relationship && (<> */}
                        {completed === true && <CheckCircleOutlineRoundedIcon/>}
                        {completed !== true && <RadioButtonUncheckedRoundedIcon/>}
                    {/* </>)} */}
                    {String(index+1).padStart(2, '0')}.
                </ListItemDecorator>
                <ListItemContent>
                    {title}
                </ListItemContent>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    [`.${accordionDetailsClasses.content}`]: {
                      p: 0,
                    }
                  }}
                >
                    {startDecorator && (<>
                    <Box sx={{px: 1, pt: 1.5, pb: 1}}>
                        {startDecorator}
                    </Box>
                    <ListDivider/>
                    </>)}

      {children !== null && (<>
      {children}
      </>)}
      {/*
      <Box sx={{px: 1, pt: 1.5, pb: 2}}>
        {description}
      </Box>
      */}

      {/* <CardOverflow> */}
      {/* </CardOverflow> */}
      {/* <CardOverflow> */}
      {columns.length > 0 && (
      <Table
        stickyHeader
        stripe="odd"
        // hoverRow
        sx={{
          margin: 0,
          padding: 0,
          tableLayout: 'auto',
          'thead th': {
            borderRadius: '0 !important',
          },
          'tbody td': {
            // borderWidth: '0 !important',
            verticalAlign: 'top',
          },
        }}
      >
        <thead>
          <tr>
            {columns.map((header:IColumn) => {
              if(header.hasOwnProperty('fn')) {
                header = header.fn({row:{}});
              }
              const required = isColumnRequired(header);
              return <th key={header.label} style={{width: header?.width ?? `${100 / columns.length}%`}}>{header.label}{required ? <RequiredAsterisk/> : null}</th>
            })}
            <th className="min"></th>
          </tr>
        </thead>
        <tbody>
          {!isTableEnabled && restProps?.disabledMessage && (
            <tr>
              <td colSpan={columns.length+1}>
                {restProps.disabledMessage}
              </td>
            </tr>
          )}
        {paginatedRelationship.map((row, rowIndex:number) => {
          const isNew = !!row.is_unsaved;
          const formData = data[rowIndex] ?? null;
          const isDeleted = data[rowIndex]?.to_be_deleted ?? false;
          // console.log(formData)

          return (
            <tr key={rowIndex} className={`${isDeleted && 'to-delete'}`}>
              {columns.map((cell:IColumn, cellIndex:number) => {
                if(cell.hasOwnProperty('fn')) {
                  cell = cell.fn({row});
                }
                // console.log(cell);
                const isEditable = isNew || cell.editableAfterSave !== false;

                let cellType = cell?.type ?? 'text';
                let cellSelectOptions = [];
                if(cell.hasOwnProperty('options')) {
                  cellType = 'select';
                  cellSelectOptions = typeof cell.options === 'function' ? cell.options(formData, getValues()) : cell.options;
                }
                let cellEnabled = !isDeleted;
                if(!isDeleted && cell.hasOwnProperty('enabled')) {
                  cellEnabled = typeof cell.enabled === 'function' ? cell.enabled(formData, getValues()) : cell.enabled;
                }

                // console.log(cellType);

                // type: 'relationship',
                // endpoint: 'properties',

                let pivotValue = null;
                let pivotName = null;
                // console.log(cell);
                let cellName = cell?.column ?? cell?.name ?? '';
                if(cellName && cellName.indexOf('pivot.') !== -1) {
                  pivotName = cellName.split('pivot.')[1];
                  pivotValue = formData?.pivot?.[pivotName];// ?? null;
                }

                // console.log(pivotName);
                // const pivotValue = cell?.name?.indexOf('pivot.') !
                // const cellValue;
                //pivot.value
                if(!cellName && cell.type !== 'custom') {
                  console.log(cell);

                  console.warn('Empty cellName typically indicates column prop isn\'t being passed. Check columnHelpers');
                }

                let hydrationData = [];
                if(!cell.hasOwnProperty('hydrate') || cell?.hydrate === true) {
                  const { to_be_deleted, is_unsaved, pivot, ...otherProps } = data[rowIndex];
                  if(/*data[rowIndex].is_unsaved && !*/Object.keys(otherProps).length) {
                    //?.[cellName]
                    if(cell?.hydrateColumn) {
                      if(formData?.[cell.hydrateColumn]) {
                        hydrationData = [formData[cell.hydrateColumn]];
                      }
                    } else {
                      hydrationData = [data[rowIndex]];
                    }
                  }
                }

                let nameForCell = null;
                let valueForCell = null;
                if(cellName && cellName.includes('pivot.')) {
                  nameForCell = cellName.split('pivot.')[1];
                  valueForCell = formData?.pivot?.[pivotName];// ?? null;
                } else {
                  nameForCell = cellName;
                  valueForCell = formData?.[nameForCell];

                  if(cellType === 'select' && cell?.multiple) {
                    try {
                      valueForCell = formData?.[nameForCell];//.filter(i => typeof i !== 'undefined').map(i => i.id);
                    } catch(e) {
                      // console.log(formData?.[nameForCell]);

                    }
                  }
                }

                let additionalInputProps = {};
                if(cell.hasOwnProperty('minTime')) {
                  additionalInputProps.minTime = cell.minTime;
                }


                const required = isColumnRequired(cell) ? 'required' : null;
                // todo: modifiers for cell values

                return (
                  <td key={`${rowIndex}-${cellIndex}`} className={cell?.class}>
                    {isEditable === true && (<>
                      {/*
                      <Controller
                        name={name}
                        control={control}
                        // rules={{ required: true }}
                        render={({ field: {ref, ...fieldProps} }) => (
                          <SearchSelect
                            {...fieldProps}
                            endpoint={restProps.endpoint}
                            hydrateWith={hydrateWith}
                            // hydrateWith={getValues().spouse ? [getValues().spouse] : []}
                          />
                        )}
                      />
                      */}
                      {cellType === 'relationship' && (
                          <SearchSelect
                            key={`${cellIndex}-${cell.endpoint}`}
                            // {...fieldProps}
                            endpoint={cell.endpoint}
                            include={cell?.include}
                            disabled={!cellEnabled}
                            onChange={(val, objVal) => setRowValue(rowIndex, cellName === 'id' ? null : cellName, objVal)}
                            value={formData?.[cellName] ?? ''}
                            required={required}
                            groupBy={cell?.groupBy}
                            // hydrateWith={hydrateWith}
                            hydrateWith={hydrationData}
                            loadAll={cell?.loadAll}
                            multiple={cell?.multiple}
                          />
                      )}
                          {cellType === 'polymorphic' && (
                              <SearchSelect
                                key={`${cellIndex}-polymorphic`}
                                endpoint={(cell.endpoints ?? []).join(',')}
                                include={cell?.include}
                                disabled={!cellEnabled}
                                onChange={(val, objVal) => {
                                  setRowValue(rowIndex, cellName, val);
                                  setRowValue(rowIndex, cell.typeColumn, morphTypeForEndpoint(objVal?.__endpoint));
                                  setRowValue(rowIndex, cell.relationship, objVal ?? null);
                                }}
                                value={formData?.[cellName] ?? ''}
                                valueEndpoint={endpointForMorphType(formData?.[cell.typeColumn])}
                                hydrateWith={
                                  formData?.[cell.relationship]
                                    ? [{...formData[cell.relationship], __endpoint: endpointForMorphType(formData?.[cell.typeColumn])}]
                                    : []
                                }
                              />
                          )}
                      {cellType === 'select' && (
                        <Select
                          key={cellIndex}
                          variant="outlined"
                          placeholder={cell?.placeholder ?? "Choose..."}
                          disabled={!cellEnabled}
                          multiple={cell?.multiple}
                          renderValue={cell?.renderValue}
                          value={valueForCell ?? (cell?.multiple ? [] : null)}
                          required={required}
                          onChange={(e, v) => {
                            if(typeof cell.onChange === 'function') {
                              cell.onChange({object: getValues(), value: v, rowIndex, setRowValue});
                            } else {
                              setRowValue(rowIndex, cellName, v)
                            }
                          }}
                          // value={}
                          // id={name}
                          // {...register(name, config)}
                          // defaultValue={defaultValue}
                          // {...restProps}
                          // name={name}
                        >
                          {cellSelectOptions.map((o, index) => (
                            <Option key={index} value={cell?.objectAsValue ? o : (o.hasOwnProperty('id') ? o.id : o.value)}>
                              {cell?.multiple && (
                                <Checkbox checked={Array.isArray(valueForCell) && valueForCell.indexOf(o.id) !== -1}/>
                              )}
                              {o?.name ?? o?.address ?? o?.unit ?? o.label}
                            </Option>
                          ))}
                        </Select>
                      )}
                      {cellType === 'tag' && (
                        <TagSelect
                          key={`${cellIndex}-${cell.endpoint}`}
                          // {...fieldProps}
                          // endpoint={cell.endpoint}
                          // include={cell?.include}
                          disabled={!cellEnabled}
                          onChange={(val, objVal) => setRowValue(rowIndex, cellName === 'id' ? null : cellName, objVal)}
                          value={formData?.[cellName] ?? []}
                          // required={required}
                          // groupBy={cell?.groupBy}
                          // hydrateWith={hydrateWith}
                          options={hydrationData}
                          // loadAll={cell?.loadAll}
                        />
                      )}
                      {cellType === 'boolean' && (
                        <Typography sx={{userSelect: 'none',}} component="label" startDecorator={
                          <Switch
                            key={cellIndex}
                            variant={cellEnabled ? 'solid' : 'outlined'}
                            disabled={!cellEnabled}
                            // id={name}
                            // type={type}
                            // {...register(name, config)}

                            // defaultValue={defaultValue}
                            // {...onChange}
                            onChange={(e) => {
                              // setValue(name, !(!!value))
                              // console.log(e.target.value)
                              setRowValue(rowIndex, cellName, !valueForCell ? 1 : 0)

                              if(typeof cell.onChange === 'function') {
                                cell.onChange(formData, data, (_cell, val) => setRowValue(rowIndex, _cell, val));
                              }
                            }}
                            checked={!!valueForCell}
                            // checked={restProps?.reverse ? !(!!value) : !!value}
                          />
                        }>
                          {cell.label}
                          {/* {...restProps.inputLabel ?? ''} */}
                        </Typography>
                      )}
                      {cellType === 'date' && (
                        <Input
                          key={cellIndex}
                          variant="outlined"
                          placeholder={cell?.placeholder ?? `${cell.label}...`}
                          disabled={!cellEnabled}
                          value={valueForCell ?? ''}
                          required={required}
                          onChange={(e) => /*setRowPivotValue*/ setRowValue(rowIndex, cellName, e.target.value)}
                          type={'date'}
                          // {...(cell?.inputType === 'number') ? {step: 'any'} : {}}
                          startDecorator={cell?.startDecorator}
                          endDecorator={cell?.endDecorator}
                        />
                      )}
                      {cellType === 'date-disabled' && (
                        <DatePicker
                          key={cellIndex}
                          formatDensity="spacious"
                          // defaultValue={new Date()}
                          value={valueForCell ? ensureDateObject(valueForCell) : null}
                          required={required}
                          // onChange={(e) => /*setRowPivotValue*/ setRowValue(rowIndex, pivotName, e.target.value)}
                          onChange={(value) => {
                            /*setRowPivotValue*/ setRowValue(rowIndex, cellName, value.target.value /*formatDateForDatabase(value)*/)
                          }}
                          // {...fieldProps}
                        />
                      )}
                      {cellType === 'time' && (
                        <TimePicker
                          key={cellIndex}
                          formatDensity="spacious"
                          // defaultValue={new Date()}
                          value={valueForCell ? dateFromTime(valueForCell) : null}
                          required={required}
                          // onChange={(e) => /*setRowPivotValue*/ setRowValue(rowIndex, pivotName, e.target.value)}
                          onChange={(value) => {
                            if(value) {
                              value = formatTimeForDatabase(value);
                            }
                            /*setRowPivotValue*/ setRowValue(rowIndex, cellName, value)
                          }}
                          {...additionalInputProps}
                          // {...fieldProps}
                        />
                      )}
                      {cellType === 'text' && (
                        <Input
                          key={cellIndex}
                          variant="outlined"
                          placeholder={cell?.placeholder ?? `${cell.label}...`}
                          disabled={!cellEnabled}
                          value={valueForCell ?? ''}
                          required={required}
                          onChange={(e) => /*setRowPivotValue*/ setRowValue(rowIndex, cellName, e.target.value)}
                          type={cell?.inputType ?? 'text'}
                          {...(cell?.inputType === 'number') ? {step: 'any'} : {}}
                          startDecorator={cell?.startDecorator}
                          endDecorator={cell?.endDecorator}
                        />
                      )}
                      {cellType === 'textarea' && (
                        <Textarea
                          key={cellIndex}
                          variant="outlined"
                          minRows={1}
                          maxRows={3}
                          // key={`${rowIndex}-note`}
                          placeholder={cell?.placeholder ?? `${cell.label}...`}
                          disabled={!cellEnabled}
                          value={valueForCell ?? ''}
                          required={required}
                          onChange={(e) => setRowValue(rowIndex, cellName, e.target.value)}
                        />
                      )}
                      {cellType === 'custom' && (<>
                        {cell?.render?.edit({rowIndex, row, data, setRowValue})}
                      </>)}
                    </>)}
                    {isEditable !== true && (<>
                      {cell.value && row[cell.value]}
                      {cell?.render?.editDisabled && cell.render.editDisabled(row, data)}
                    </>)}
                  </td>
                )
              })}
              <td className="min">
                <DeleteRowButton data={data} index={rowIndex} toggle={markRowForDeletion}/>
                {typeof restProps.additionalActions !== 'undefined' && restProps.additionalActions({row, data, getValues, setValue})}
              </td>
            </tr>
          )
        })}

          {/* {paginatedRelationship.length === 0 && ( */}
          <tr>
              <td colSpan={columns.length+1}>
                <EmptyAddAction/>
              </td>
            </tr>
          {/* )} */}
        </tbody>
      </Table>
      )}
      {/* </CardOverflow> */}


        {endDecorator && (<>
            <ListDivider/>
            <Box sx={{px: 1, pt: 0, pb: 1.5}}>
              {endDecorator}
            </Box>
          </>)}
        </AccordionDetails>
    </Accordion>
  );
}));

export default RapidReinvestmentSection;
